import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Eye, 
  EyeOff, 
  Save, 
  X,
  Target,
  Award,
  Users
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAboutSection = () => {
  const [aboutSections, setAboutSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const [newSection, setNewSection] = useState({
    title: '',
    subtitle: '',
    description: '',
    mission_statement: '',
    vision_statement: '',
    values: [],
    team_info: '',
    company_story: '',
    achievements: [],
    certifications: [],
    is_published: false,
    display_order: 1
  });

  const [tempArrays, setTempArrays] = useState({
    values: '',
    achievements: '',
    certifications: ''
  });

  useEffect(() => {
    fetchAboutSections();
  }, []);

  const fetchAboutSections = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/about/all`);
      setAboutSections(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching about sections:', error);
      setLoading(false);
    }
  };

  const handleAddSection = async () => {
    try {
      // Convert comma-separated strings to arrays
      const sectionData = {
        ...newSection,
        values: tempArrays.values ? tempArrays.values.split(',').map(v => v.trim()).filter(v => v) : [],
        achievements: tempArrays.achievements ? tempArrays.achievements.split(',').map(a => a.trim()).filter(a => a) : [],
        certifications: tempArrays.certifications ? tempArrays.certifications.split(',').map(c => c.trim()).filter(c => c) : []
      };

      const response = await axios.post(`${BACKEND_URL}/api/about`, sectionData);
      setAboutSections([...aboutSections, response.data]);
      
      // Reset form
      setNewSection({
        title: '',
        subtitle: '',
        description: '',
        mission_statement: '',
        vision_statement: '',
        values: [],
        team_info: '',
        company_story: '',
        achievements: [],
        certifications: [],
        is_published: false,
        display_order: 1
      });
      setTempArrays({ values: '', achievements: '', certifications: '' });
      setShowAddForm(false);
      alert('About section added successfully!');
    } catch (error) {
      console.error('Error adding about section:', error);
      alert('Error adding about section');
    }
  };

  const handleUpdateSection = async (id, updatedData) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/about/${id}`, updatedData);
      setAboutSections(aboutSections.map(s => s.id === id ? response.data : s));
      setEditingSection(null);
      alert('About section updated successfully!');
    } catch (error) {
      console.error('Error updating about section:', error);
      alert('Error updating about section');
    }
  };

  const handleDeleteSection = async (id) => {
    if (window.confirm('Are you sure you want to delete this about section?')) {
      try {
        await axios.delete(`${BACKEND_URL}/api/about/${id}`);
        setAboutSections(aboutSections.filter(s => s.id !== id));
        alert('About section deleted successfully!');
      } catch (error) {
        console.error('Error deleting about section:', error);
        alert('Error deleting about section');
      }
    }
  };

  const toggleSectionVisibility = async (section) => {
    const updatedSection = { is_published: !section.is_published };
    await handleUpdateSection(section.id, updatedSection);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p>Loading about sections...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">Manage About Section</h3>
        <Button onClick={() => setShowAddForm(true)} className="flex items-center gap-2">
          <Plus size={16} />
          Add About Section
        </Button>
      </div>

      <div className="text-sm text-gray-600 mb-4">
        <Badge variant="secondary">Total: {aboutSections.length}</Badge>
        <Badge variant="default" className="ml-2">Published: {aboutSections.filter(s => s.is_published).length}</Badge>
        <Badge variant="outline" className="ml-2">Hidden: {aboutSections.filter(s => !s.is_published).length}</Badge>
      </div>

      {/* Add New Section Form */}
      {showAddForm && (
        <Card className="border-green-200">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Add New About Section</CardTitle>
              <Button onClick={() => setShowAddForm(false)} variant="ghost" size="sm">
                <X size={16} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                placeholder="Section Title"
                value={newSection.title}
                onChange={(e) => setNewSection({...newSection, title: e.target.value})}
              />
              <Input
                placeholder="Subtitle (optional)"
                value={newSection.subtitle}
                onChange={(e) => setNewSection({...newSection, subtitle: e.target.value})}
              />
            </div>
            
            <Textarea
              placeholder="Main description..."
              value={newSection.description}
              onChange={(e) => setNewSection({...newSection, description: e.target.value})}
              rows={3}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Textarea
                placeholder="Mission statement (optional)..."
                value={newSection.mission_statement}
                onChange={(e) => setNewSection({...newSection, mission_statement: e.target.value})}
                rows={3}
              />
              <Textarea
                placeholder="Vision statement (optional)..."
                value={newSection.vision_statement}
                onChange={(e) => setNewSection({...newSection, vision_statement: e.target.value})}
                rows={3}
              />
            </div>

            <Textarea
              placeholder="Company story (optional)..."
              value={newSection.company_story}
              onChange={(e) => setNewSection({...newSection, company_story: e.target.value})}
              rows={3}
            />

            <Textarea
              placeholder="Team information (optional)..."
              value={newSection.team_info}
              onChange={(e) => setNewSection({...newSection, team_info: e.target.value})}
              rows={2}
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium">Company Values (comma-separated)</label>
              <Textarea
                placeholder="Quality, Innovation, Customer Focus, etc."
                value={tempArrays.values}
                onChange={(e) => setTempArrays({...tempArrays, values: e.target.value})}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">Key Achievements (comma-separated)</label>
              <Textarea
                placeholder="50+ websites launched, 100% client satisfaction, etc."
                value={tempArrays.achievements}
                onChange={(e) => setTempArrays({...tempArrays, achievements: e.target.value})}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">Certifications & Expertise (comma-separated)</label>
              <Textarea
                placeholder="Google Analytics Certified, React Specialist, etc."
                value={tempArrays.certifications}
                onChange={(e) => setTempArrays({...tempArrays, certifications: e.target.value})}
                rows={2}
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={newSection.is_published}
                  onChange={(e) => setNewSection({...newSection, is_published: e.target.checked})}
                />
                <label htmlFor="published">Publish on website</label>
              </div>
              
              <div className="flex items-center gap-2">
                <label>Display Order:</label>
                <Input
                  type="number"
                  min="1"
                  value={newSection.display_order}
                  onChange={(e) => setNewSection({...newSection, display_order: parseInt(e.target.value) || 1})}
                  className="w-20"
                />
              </div>
            </div>

            <Button onClick={handleAddSection} className="w-full">
              <Save size={16} className="mr-2" />
              Add About Section
            </Button>
          </CardContent>
        </Card>
      )}

      {/* About Sections List */}
      {aboutSections.map((section) => (
        <Card key={section.id} className={`${section.is_published ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
          <CardContent className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-2">
                  <Target size={24} style={{ color: 'var(--brand-primary)' }} />
                  <div>
                    <h3 className="font-semibold text-lg">{section.title}</h3>
                    {section.subtitle && (
                      <p className="text-gray-600 text-sm">{section.subtitle}</p>
                    )}
                  </div>
                </div>
                <p className="text-gray-700 mb-3">{section.description.substring(0, 200)}...</p>
                
                {/* Content Preview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  {section.values && section.values.length > 0 && (
                    <div>
                      <strong>Values:</strong> {section.values.length} items
                    </div>
                  )}
                  {section.achievements && section.achievements.length > 0 && (
                    <div>
                      <strong>Achievements:</strong> {section.achievements.length} items
                    </div>
                  )}
                  {section.certifications && section.certifications.length > 0 && (
                    <div>
                      <strong>Certifications:</strong> {section.certifications.length} items
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2 ml-4">
                <Badge variant={section.is_published ? "default" : "secondary"}>
                  {section.is_published ? "Published" : "Hidden"}
                </Badge>
                <Button
                  onClick={() => toggleSectionVisibility(section)}
                  variant="outline"
                  size="sm"
                >
                  {section.is_published ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
                <Button
                  onClick={() => setEditingSection(section)}
                  variant="outline"
                  size="sm"
                >
                  <Edit3 size={16} />
                </Button>
                <Button
                  onClick={() => handleDeleteSection(section.id)}
                  variant="destructive"
                  size="sm"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {aboutSections.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Users size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">No About Sections</h3>
            <p className="text-gray-600 mb-4">Create your first about section to tell your company story.</p>
            <Button onClick={() => setShowAddForm(true)}>
              <Plus size={16} className="mr-2" />
              Add First About Section
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminAboutSection;